import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { PickingItem } from '@app/interfaces/purchase-order.model';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { EOrderShippingErrors } from 'v2/modules/order/interfaces/order-shipping.interface';
import {
    ISalesOrder,
    ESalesOrderStatus,
    ESalesOrderSource,
} from 'v2/modules/order/interfaces/order.interface';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IAppIntegrationAccount } from 'v2/modules/shared/interfaces/app-integration-account.interface';
import { DecimalPlacesPipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { FulfillmentOrderApiService } from 'v2/modules/shared/services/api/fulfillment-order-api.service';
import { LotApiService } from 'v2/modules/shared/services/api/lot-api.service';
import { OrderApiService } from 'v2/modules/shared/services/api/order/order-api.service';
import { SalesOrderApiService } from 'v2/modules/shared/services/api/sales-order-api.service';
import { OrderUtilService } from 'v2/modules/shared/services/util/order-util.service';
import {
    trackByControlValue,
    trackById,
} from 'v2/modules/shared/util/track-by.util';
import { EShippingStatus } from 'v2/modules/shipping/interfaces/shipping.interface';
import { EAppAccountType } from 'v2/modules/system-setting/constants/app-integration-account.enum';
import { EChannelType } from 'v2/modules/channel/contants/channel.constant';

//

interface IOrderData extends ISalesOrder {
  errors ?:any;
  shippingStatus ?:string;
  shippingError ?:string;
  pickingItems ?:PickingItem[];
}

@Component({
  selector: 'lum-order-ship-form-v2',
  templateUrl: './order-ship-form-v2.component.html',
  styleUrls: ['./order-ship-form-v2.component.scss'],
})
export class OrderShipFormV2Component implements OnInit, OnDestroy {
  loading :boolean = false;
  lots :any[] = [];
  shippingStatus = EShippingStatus;
  orderShippingErrors = EOrderShippingErrors;
  shippableOrders :IOrderData[] = [];
  nonShippableOrders :IOrderData[] = [];
  salesOrderStatus = ESalesOrderStatus;
  formData :UntypedFormGroup;
  supportedDestinationOmsAppTypes :EAppAccountType[] = [
    EAppAccountType.SHIPSTATION,
    EAppAccountType.TRACKSTAR,
    EAppAccountType.EDI,
  ];
  showDestinationOmsStoresDropdown :boolean = false;
  selectedDestinationOms :Partial<IAppIntegrationAccount>;
  loadingPickingItems :boolean = false;

  ftPushUnPushToFulfillmentChannel :boolean = false;
  ftChannelTypes :boolean = false;

  // Channel types for fulfillment modal
  fulfillmentChannelTypes :string[] = [EChannelType.FULFILLMENT_CHANNEL, EChannelType.END_TO_END_CHANNEL];

  protected readonly trackByControlValue = trackByControlValue;

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private dialogRef :MatDialogRef<OrderShipFormV2Component>,
    @Inject(MAT_DIALOG_DATA)
    public data :{
      title :string;
      salesOrders :ISalesOrder[];
      isSelectAllRows :boolean;
      ftEdiIntegrationEnabled :boolean;
      fulfillmentOrder :any;
      isCreateFulfillmentOrder :boolean;
    },
    private orderApiService :OrderApiService,
    private snackBar :MatSnackBar,
    private orderUtilService :OrderUtilService,
    private fb :UntypedFormBuilder,
    private salesOrderApiService :SalesOrderApiService,
    private decimalPlacesPipe :DecimalPlacesPipe,
    private featureToggleService :FeatureToggleService,
    private fulfillmentOrderApiService :FulfillmentOrderApiService,
    private lotApiService :LotApiService,
  ) {}

  ngOnInit() :void {
    this.loadFeatureFlag();
    this.initSalesOrders();
    this.#loadSalesOrderPickingItems();
    this.initFormData();
  }

  private getNextOrderKey(salesOrderId :number) :void {
    this.loading = true;

    this.fulfillmentOrderApiService
      .getNextOrderKey({ salesOrderId })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (fulfillmentOrderKey :string | null) => {
          if (fulfillmentOrderKey) {
            this.formData.patchValue({ fulfillmentOrderKey });
          }
        },
      });
  }

  private loadFeatureFlag() :void {
    this.ftPushUnPushToFulfillmentChannel = this.featureToggleService.isActive(
      'red_DV-3334_push_unpush_fulfillment_channel',
    );
    this.ftChannelTypes = this.featureToggleService.isActive(
      'red_DV-3395_channel_types',
    );
  }

  private initFormData() :void {
    this.formData = this.fb.group({
      warehouseGroupId: [null],
      destinationOmsId: [null],
      destinationOmsStoreId: [null],
      pushToOms: [true], // default on
      fulfillmentOrderKey: [null],
      orderItems: this.fb.array([]),
      fulfillmentChannelId: [null],
    });

    const shippableOrder = this.shippableOrders[0];

    if (this.canOverride) {
      const hasPushOrderIntegrationMapping = !!(
        shippableOrder?.channel?.push_order_integration_mapping ||
        shippableOrder?.channel?.pushOrderIntegrationMapping
      );

      let warehouseGroupId =
        shippableOrder?.channel?.warehouse_group_id ??
        shippableOrder?.channel?.warehouseGroupId;

      // For Transfer Orders, override and always use the source warehouse's group
      if (shippableOrder.source === ESalesOrderSource.TRANSFER_ORDER) {
        warehouseGroupId =
          shippableOrder?.warehouse_group_id ?? shippableOrder?.warehouseGroupId;
      }

      this.formData.patchValue({
        ...(this.ftChannelTypes
          ? { fulfillmentChannelId: shippableOrder?.channel?.id }
          : {
              warehouseGroupId,
              destinationOmsId:
                shippableOrder?.channel?.push_order_integration_mapping
                  ?.app_integration_account_id ??
                shippableOrder?.channel?.pushOrderIntegrationMapping
                  ?.appIntegrationAccountId,
              destinationOmsStoreId:
                shippableOrder?.channel?.push_order_integration_mapping
                  ?.oms_store_id ??
                shippableOrder?.channel?.pushOrderIntegrationMapping?.omsStoreId,
            }),
      });

      const pushToOmsControl = this.formData.get('pushToOms');
    }

    if (this.fulfillmentOrder?.id) {
      this.formData.patchValue({
        warehouseGroupId: this.fulfillmentOrder?.warehouseGroupId,
        destinationOmsId: this.fulfillmentOrder?.destinationOmsId,
        destinationOmsStoreId: this.fulfillmentOrder?.destinationOmsStoreId,
        pushToOms: this.fulfillmentOrder?.pushToOms ?? true, // on by default
        fulfillmentOrderKey: this.fulfillmentOrder?.orderKey,
        fulfillmentChannelId: this.fulfillmentOrder?.fulfillmentChannelId ?? null,
      });
      // disable push to oms when update
      if (this.fulfillmentOrder.pushedAt) {
        this.formData.get('pushToOms')?.disable();
      }
    } else {
      this.getNextOrderKey(shippableOrder.id);
    }

    this.handlePushToOmsChange();
    this.formData.get('pushToOms')!.valueChanges.subscribe(() => {
      this.handlePushToOmsChange();
    });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onConfirmShipOrders() :void | boolean {
    if (this.data.isSelectAllRows) {
      const includedItemIdsPayload = [];
      this.shippableOrders
        .filter(
          (order :IOrderData) => order.shippingStatus !== ESalesOrderStatus.SHIPPED,
        )
        .forEach((order :IOrderData) => {
          const includedItemIds = order.orderItems
            ?.filter((item) => !item.isIncluded)
            .map((item) => {
              return item.id;
            });
          includedItemIdsPayload.push(...includedItemIds);
        });

      this.dialogRef.close(includedItemIdsPayload);

      return;
    }
    if (this.nonShippableOrders.length > 0) {
      return true;
    }
    this.loading = true;

    this.shippableOrders
      .filter((order :any) => order.shippingStatus !== ESalesOrderStatus.SHIPPED)
      .forEach((order :IOrderData) => {
        const excludedAllItems = order?.orderItems?.every(
          (item) => !item.isIncluded,
        );
        if (excludedAllItems) {
          this.loading = false;

          return;
        }
        order.shippingStatus = EShippingStatus.SHIPPING;

        // Find all selected parent item IDs
        const includedParentIds = order.orderItems
          ?.filter(
            (item) =>
              item.isIncluded &&
              item.status !== ESalesOrderStatus.SHIPPED &&
              item.id === (item.parent_id || item.id),
          )
          .map((item) => item.id);

        // Include parent + all of its children (if any)
        const includedOrderItemIds = order.orderItems
          ?.filter(
            (item) =>
              item.status !== ESalesOrderStatus.SHIPPED &&
              includedParentIds.includes(item.parent_id || item.id),
          )
          .map((item) => item.id);

        const includedOnlyParentItemIds = order.orderItems
          ?.filter(
            (item) =>
              item.status !== ESalesOrderStatus.SHIPPED &&
              includedParentIds.includes(item.id) &&
              (item.parent_id === null ||
                item.parent_id === undefined ||
                item.id === item.parent_id),
          )
          .map((item) => item.id);

        // Build payload
        const orderItems = order.orderItems
          ?.filter(
            (item) =>
              item.status !== ESalesOrderStatus.SHIPPED &&
              includedParentIds.includes(item.parent_id || item.id),
          )
          .map((item) => ({
            id: item.parent_id !== null ? item.parent_id : item.id,
            quantity: item.quantityOverridden,
            lotId: item?.lot?.id ?? null,
          }));

        if (includedOrderItemIds.length <= 0) {
          this.loading = false;

          return;
        }

        const invalidQuantityOrderItemIds = order.orderItems?.filter(
          (item) =>
            item.isIncluded &&
            item.status !== ESalesOrderStatus.SHIPPED &&
            (item.quantityOverridden < 1 ||
              item.quantityOverridden > item.quantityRemaining),
        );

        if (invalidQuantityOrderItemIds?.length) {
          this.snackBar.open('Invalid quantity input.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          order.shippingStatus = EShippingStatus.FAILED;
          this.loading = false;

          return;
        }

        let payload :Record<string, any> = {
          includedOrderItemIds: includedOnlyParentItemIds,
        };

        if (this.canOverride) {
          payload = {
            ...payload,
            override: true,
            ...(this.ftChannelTypes
              ? { fulfillmentChannelId: this.formData.value.fulfillmentChannelId }
              : {
                  warehouseGroupId: this.formData.value.warehouseGroupId,
                  destinationOmsId: this.formData.value.destinationOmsId,
                  destinationOmsStoreId: this.formData.value.destinationOmsStoreId,
                }),
            pushToOms: this.formData.value.pushToOms,
            fulfillmentOrderKey: this.formData.value.fulfillmentOrderKey,
            orderItems,
            ...(this.fulfillmentOrder?.id
              ? { fulfillmentOrderId: this.fulfillmentOrder.id }
              : {}),
          };
        }

        this.orderApiService
          .shipOrderToShipstation(order.id, payload)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe({
            next: (response) => {
              order.shippingStatus = EShippingStatus.SHIPPED;
              if (includedOrderItemIds.length > 0) {
                order.orderItems?.forEach((item) => {
                  if (includedOrderItemIds.includes(item.id)) {
                    item.status = ESalesOrderStatus.AWAITING_SHIPMENT;
                    item.quantityFulfilled +=
                      item.quantityOverridden -
                      (item.quantityOriginalOverriddenValue ?? 0);
                    item.errorMessage = null;
                  }
                });
              }
              if (this.finishedShippingOrders) {
                this.snackBar.open('Pushing orders finished.', 'Close', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.onClose();
              }
            },
            error: (err) => {
              order.shippingStatus = EShippingStatus.FAILED;
              if (includedOrderItemIds.length > 0) {
                order.orderItems?.forEach((item) => {
                  const data = err?.data?.find(
                    (error) =>
                      error.id === item.id &&
                      includedOrderItemIds.includes(error.id),
                  );
                  if (data) {
                    item.errorMessage = data.error;
                  }
                });
              }
              if (this.finishedShippingOrders) {
                this.snackBar.open(
                  err?.error ? err?.error : 'Failed to push some orders.',
                  'Close',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
                this.onClose();
              }
            },
          });
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onToggleItem(orderId :number, itemId :number) :void {
    const order = this.shippableOrders?.find((order) => order.id === orderId);
    if (!order) {
      return;
    }

    order.orderItems?.forEach((item) => {
      if (item.id === itemId) {
        item.isIncluded = !item.isIncluded;
      }
    });
    order.isIncluded = order.orderItems
      ?.filter((item) => item.status === ESalesOrderStatus.PENDING)
      .some((item) => item.isIncluded);
  }

  onToggleOrder(orderId :number) :void {
    this.shippableOrders?.forEach((order) => {
      if (order.id === orderId) {
        order.isExpanded = !order.isExpanded;
      }
    });
  }

  onToggleAllOrderItems(orderId :number, checked :boolean) :void {
    const order = this.shippableOrders?.find((order) => order.id === orderId);
    if (!order) {
      return;
    }

    // Get all selectable items in the order
    const selectableItems = order.orderItems?.filter((item) => 
      item.id === (item.parent_id || item.id) &&
      item.status !== this.salesOrderStatus.SHIPPED &&
      item.quantityRemaining !== 0
    ) || [];

    // Toggle all selectable items
    selectableItems.forEach((item) => {
      item.isIncluded = checked;
    });

    // Update order's isIncluded status
    order.isIncluded = order.orderItems
      ?.filter((item) => item.status === ESalesOrderStatus.PENDING)
      .some((item) => item.isIncluded) || false;
  }

  isAllOrderItemsSelected(order :any) :boolean {
    const selectableItems = order.orderItems?.filter((item) => 
      item.id === (item.parent_id || item.id) &&
      item.status !== this.salesOrderStatus.SHIPPED &&
      item.quantityRemaining !== 0
    ) || [];

    if (selectableItems.length === 0) {
      return false;
    }

    return selectableItems.every((item) => item.isIncluded);
  }

  isSomeOrderItemsSelected(order :any) :boolean {
    const selectableItems = order.orderItems?.filter((item) => 
      item.id === (item.parent_id || item.id) &&
      item.status !== this.salesOrderStatus.SHIPPED &&
      item.quantityRemaining !== 0
    ) || [];

    if (selectableItems.length === 0) {
      return false;
    }

    const selectedCount = selectableItems.filter((item) => item.isIncluded).length;
    return selectedCount > 0 && selectedCount < selectableItems.length;
  }

  hasSelectableItems(order :any) :boolean {
    return order.orderItems?.some((item) => 
      item.id === (item.parent_id || item.id) &&
      item.status !== this.salesOrderStatus.SHIPPED &&
      item.quantityRemaining !== 0
    ) || false;
  }

  onLoadLots(rfqId :number, item :any) :void {
    item.loadingLots = true;
    item.lots = [];
    this.lotApiService
      .getByRfqIds({
        rfqIds: rfqId.toString(),
        limit: '20',
        page: '1',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response.data) {
            item.lots = response.data || [];
          }
          item.loadingLots = false;
        },
        error: (err) => {
          this.snackBar.open(
            err?.error ? err?.error : 'Failed to fetch lots.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          item.loadingLots = false;
        },
      });
  }

  onSearchLot(searchQuery :string, rfqId :number, item :any) :void {
    item.loadingLots = true;
    this.lotApiService
      .getByRfqIds({
        rfqIds: rfqId.toString(),
        limit: '20',
        page: '1',
        searchQuery,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response.data) {
            item.lots = response.data || [];
          }
          item.loadingLots = false;
        },
        error: (err) => {
          this.snackBar.open(
            err?.error ? err?.error : 'Failed to fetch lots.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          item.loadingLots = false;
        },
      });
  }

  onSelectDestinationOms(
    appIntegrationAccount :Partial<IAppIntegrationAccount>,
  ) :void {
    this.selectedDestinationOms = appIntegrationAccount;
    this.handlePushToOmsChange();
  }

  #loadSalesOrderPickingItems() :void {
    this.loadingPickingItems = true;
    this.salesOrderApiService
      .getSalesOrdersPickingItems({
        salesOrderIds: this.salesOrders.map((order) => order.id).join(','),
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.shippableOrders = this.shippableOrders.map((order) => {
            let orderItems = order.orderItems || [];
            const pickingItems =
              response.find((data :any) => data.salesOrder.id === order.id)
                ?.pickingItems || [];

            if (pickingItems?.length) {
              orderItems = orderItems.map((orderItem :any) => {
                const pickingItem = pickingItems.find(
                  (item) => item.rfqId === (orderItem.rfq_id ?? orderItem.rfqId),
                );

                return {
                  ...orderItem,
                  availableQuantity: pickingItem?.availableQuantity || 0,
                  lotTracking: pickingItem?.rfq?.lot_tracking || false,
                  lot: pickingItem?.lot || null,
                  lotId: pickingItem?.lot?.id || null,
                };
              });
            }

            return {
              ...order,
              pickingItems,
              orderItems,
            };
          });
          this.loadingPickingItems = false;
        },
        error: (err) => {
          this.snackBar.open(
            err?.error ? err?.error : 'Failed to fetch picking items.',
            'Close',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this.loadingPickingItems = false;
        },
      });
  }

  splitOrderItem(order :any, item :any) :void {
    const parentId = item.id; // item is always the original parent (because that's where the button is)

    // Find all split rows (including parent)
    const splitRows = order.orderItems.filter(
      (i) => i.parent_id === parentId || i.id === parentId,
    );

    // Pick the last one (most recent split)
    const target = splitRows[splitRows.length - 1];
    const index = order.orderItems.findIndex((i) => i.id === target.id);

    const fulfilled = Number(target.quantityOverridden ?? 0);
    const remaining = Number(target.quantityRemaining ?? 0);
    const newQty = remaining - fulfilled;

    if (newQty < 1 || fulfilled <= 0 || fulfilled === remaining) return;

    // Lock the one being split
    target.quantityRemaining = fulfilled;
    target.quantityOverridden = fulfilled;
    target.isSplitSource = true;

    const splitItem = {
      ...target,
      id: Date.now() + Math.floor(Math.random() * 1000),
      parent_id: parentId, // always point to the original
      isSplitSource: false,
      quantityRemaining: newQty,
      quantityOverridden: newQty,
      errorMessage: null,
      isIncluded: true,
    };

    order.orderItems.splice(index + 1, 0, splitItem);
  }

  removeSplitItem(order :any, itemToRemove :any) :void {
    const parentId = itemToRemove.parent_id;

    const siblings = order.orderItems.filter(
      (i) => i.parent_id === parentId && i.id !== itemToRemove.id,
    );

    // Determine who will receive the quantity back
    let targetRecipient;

    if (siblings.length > 0) {
      // Send it to the last sibling (by position)
      targetRecipient = siblings[siblings.length - 1];
    } else {
      // No siblings left — send it back to parent
      targetRecipient = order.orderItems.find((i) => i.id === parentId);
    }

    if (targetRecipient) {
      targetRecipient.quantityOverridden += itemToRemove.quantityRemaining;
      targetRecipient.quantityRemaining += itemToRemove.quantityRemaining;
      targetRecipient.isSplitSource = false;
    }

    // Remove the split item
    const index = order.orderItems.findIndex((i) => i.id === itemToRemove.id);
    if (index !== -1) {
      order.orderItems.splice(index, 1);
    }

    // If no more children, clear isSplitSource flag on parent
    const stillHasChildren = order.orderItems.some(
      (i) => i.parent_id === parentId && i.id !== parentId,
    );

    if (!stillHasChildren) {
      const parent = order.orderItems.find((i) => i.id === parentId);
      if (parent) parent.isSplitSource = false;
    }
  }

  onUpdateQuantity(id :number, event :Event) :void {
    const inputElement = event.target as HTMLInputElement;
    const valueString = inputElement.value.trim();
    this.shippableOrders.forEach((order) =>
      order?.orderItems?.forEach((item) => {
        if (item.id === id) {
          item.quantityOverridden = Number(valueString) ?? 0;
        }
      }),
    );

    inputElement.value = valueString;
  }

  displayFnLot(option :any) :string {
    return option?.lot_number || option?.lotNumber;
  }

  private initSalesOrders() :void {
    this.salesOrders.forEach((order :ISalesOrder) => {
      let totalRemainingQty :number = 0;
      if (order.orderItems?.length > 0) {
        order.orderItems.forEach((item) => {
          item.isIncluded = true;
          item.ignoreItem = item.ignoreItem ?? item.ignore_item;
          item.errorMessage = null;
          item.quantityFulfilled = Number(
            item.quantityFulfilled ?? item.quantity_fulfilled,
          );

          item.quantityRemaining =
            Number(item.quantity ?? 0) - Number(item.quantityFulfilled ?? 0);
          const quantityOverriddenValue = Math.min(
            item.quantity,
            item.quantityRemaining,
          );
          item.quantityOverridden = Number(
            this.decimalPlacesPipe.transform(quantityOverriddenValue),
          );

          totalRemainingQty += Number(
            this.decimalPlacesPipe.transform(item.quantityRemaining),
          );

          if (!item.quantityRemaining) {
            // if the remaining quantity of the item is 0 then do not include.
            item.isIncluded = false;
          }

          if (
            this.fulfillmentOrder &&
            this.fulfillmentOrder.fulfillmentOrderItems?.length > 0
          ) {
            // Find matching fulfillment order item
            const fulfillmentItem =
              this.fulfillmentOrder.fulfillmentOrderItems.find(
                (fulfillmentOrderItem) =>
                  fulfillmentOrderItem.salesOrderItemId === item.id,
              );

            if (fulfillmentItem) {
              item.isIncluded = true;
              item.quantityRemaining =
                item.quantityRemaining + Number(fulfillmentItem.quantity);
              item.quantityOverridden = Number(fulfillmentItem.quantity);
              item.quantityOriginalOverriddenValue = Number(
                fulfillmentItem.quantity,
              );
            } else {
              item.isIncluded = false;
            }
          }
        });
      }
      order.hasNoRemainingFulfillmentQuantity = totalRemainingQty === 0;
      order.isIncluded = true;
      order.isExpanded = true;
      order.hasPushOrderIntegrationMapping = !!(
        order?.channel?.push_order_integration_mapping ||
        order?.channel?.pushOrderIntegrationMapping
      );

      const orderShippabilityErrors =
        this.orderUtilService.checkOrderShippabilityErrors(order);
      if (orderShippabilityErrors.isNotShippable) {
        if (
          this.data.ftEdiIntegrationEnabled &&
          order?.channel?.pushOrderIntegrationMapping
        ) {
          this.shippableOrders.push(order);
        } else {
          this.nonShippableOrders.push({
            ...order,
            ...{
              errors: orderShippabilityErrors,
            },
          });
        }
      } else {
        this.shippableOrders.push(order);
      }
    });
  }

  private selectedAppHasStores(app :string) :boolean {
    if (app.length === 0) {
      return false;
    }

    const withStoresOms :string[] = [
      EAppAccountType.SHIPKEY,
      EAppAccountType.SHIPSTATION,
      EAppAccountType.TRACKSTAR,
    ];

    return withStoresOms.includes(app);
  }

  private calculateShowDestinationOmsStoresDropdown() :boolean {
    return (
      this.selectedDestinationOms?.id &&
      this.selectedAppHasStores(this.selectedDestinationOms.app || '')
    );
  }

  private handlePushToOmsChange() :void {
    if (this.ftChannelTypes) {
      return;
    }

    const pushToOms = this.formData.get('pushToOms')!.value;
    this.showDestinationOmsStoresDropdown =
      this.calculateShowDestinationOmsStoresDropdown();
    const destinationOmsId = this.formData.get('destinationOmsId');
    const destinationOmsStoreId = this.formData.get('destinationOmsStoreId');
    if (pushToOms) {
      destinationOmsId?.setValidators([Validators.required]);
      if (
        this.showDestinationOmsStoresDropdown &&
        this.selectedDestinationOms?.app === EAppAccountType.SHIPSTATION
      ) {
        destinationOmsStoreId?.setValidators([Validators.required]);
      }
    } else {
      destinationOmsId?.clearValidators();
      destinationOmsStoreId?.clearValidators();
    }
    destinationOmsId?.updateValueAndValidity();
    destinationOmsStoreId?.updateValueAndValidity();
  }

  get salesOrders() :ISalesOrder[] {
    return this.data.salesOrders ?? [];
  }

  get fulfillmentOrder() :any {
    return this.data.fulfillmentOrder ?? {};
  }

  get finishedShippingOrders() :boolean {
    return (
      this.shippableOrders.filter(
        (order :IOrderData) => order.shippingStatus === EShippingStatus.SHIPPING,
      ).length === 0
    );
  }

  get disableConfirmShipOrders() :boolean {
    const hasNonShippableOrders = this.nonShippableOrders.length > 0;
    const noShippableOrders = this.shippableOrders.length === 0;
    const allRowsNotSelected = !this.data.isSelectAllRows;
    const allRowsSelected = this.data.isSelectAllRows;

    if (this.loading || this.hasInvalidItemQuantities) {
      return true;
    }

    if (hasNonShippableOrders) {
      if (allRowsNotSelected || (noShippableOrders && allRowsSelected)) {
        return true;
      }
    }

    if (
      this.shippableOrders?.length > 0 ||
      (this.ftPushUnPushToFulfillmentChannel && this.data.isCreateFulfillmentOrder)
    ) {
      return this.shippableOrders?.every((order) => {
        return order?.orderItems
          ?.filter((item) => item.status !== ESalesOrderStatus.SHIPPED)
          .every((item) => !item.isIncluded);
      });
    }

    return false;
  }

  get selectedDestinationOmsId() :number {
    return parseInt(this.formData.value.destinationOmsId) || null;
  }

  get destinationOmsIdControl() :UntypedFormControl {
    return this.formData?.get('destinationOmsId') as UntypedFormControl;
  }

  get destinationOmsStoreIdControl() :UntypedFormControl {
    return this.formData?.get('destinationOmsStoreId') as UntypedFormControl;
  }

  get canOverride() :boolean {
    return this.shippableOrders.length === 1;
  }

  get hasInvalidItemQuantities() :boolean {
    return this.shippableOrders.some((order) =>
      order.orderItems?.some(
        (item) =>
          item.status === ESalesOrderStatus.PENDING &&
          item.isIncluded &&
          item.quantityOverridden < 1,
      ),
    );
  }
}
